/**
 * Sécuriser sa partie, et la retrouver ailleurs.
 *
 * On joue **sans compte** : un UUID tiré à la première visite suffit, et le jeu
 * démarre en trois secondes. Mais cet UUID ne vit que dans le navigateur : un
 * nettoyage, un changement de téléphone, et des mois de jeu disparaissent.
 *
 * Le joueur peut donc, quand il le veut, attacher un pseudonyme et un mot de
 * passe à sa partie. Aucune adresse de courriel n'est demandée : elle imposerait
 * un service d'envoi tiers, là où le projet tient sur Cloudflare seul, et
 * obligerait à conserver une donnée personnelle dont le jeu n'a aucun usage.
 */
import type { Etat } from "../sim/etat.ts"

const CLE_ID = "dofusidle.compte"
const CLE_PSEUDO = "dofusidle.pseudo"

export const pseudoLocal = (): string | null => localStorage.getItem(CLE_PSEUDO)

/** Ce que le serveur renvoie quand ça se passe mal, déjà en français. */
async function erreurDe(r: Response): Promise<string> {
  try {
    const d = (await r.json()) as { erreur?: string }
    return d.erreur ?? "Le serveur n'a pas répondu comme prévu."
  } catch {
    return "Le serveur n'a pas répondu comme prévu."
  }
}

/** Attache un pseudonyme et un mot de passe à la partie en cours. */
export async function securiser(
  id: string, pseudo: string, motdepasse: string,
): Promise<{ ok: true } | { ok: false; erreur: string }> {
  try {
    const r = await fetch("/api/compte", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ id, pseudo, motdepasse }),
    })
    if (!r.ok) return { ok: false, erreur: await erreurDe(r) }
    localStorage.setItem(CLE_PSEUDO, pseudo)
    return { ok: true }
  } catch {
    return { ok: false, erreur: "Pas de réseau : réessaie dans un instant." }
  }
}

/**
 * Récupère une partie sécurisée et l'adopte sur cet appareil.
 *
 * L'appelant reçoit l'état : c'est lui qui décide de l'installer, parce que
 * cela écrase la partie locale et que ce choix ne se prend pas ici.
 */
export async function connecter(
  pseudo: string, motdepasse: string,
): Promise<{ ok: true; id: string; etat: Etat; version: number } | { ok: false; erreur: string }> {
  try {
    const r = await fetch("/api/connexion", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ pseudo, motdepasse }),
    })
    if (!r.ok) return { ok: false, erreur: await erreurDe(r) }
    const d = (await r.json()) as { id: string; save: Etat; version: number }
    return { ok: true, id: d.id, etat: d.save, version: d.version }
  } catch {
    return { ok: false, erreur: "Pas de réseau : réessaie dans un instant." }
  }
}

/** Installe la partie récupérée à la place de celle de cet appareil. */
export function adopter(id: string, pseudo: string): void {
  localStorage.setItem(CLE_ID, id)
  localStorage.setItem(CLE_PSEUDO, pseudo)
}

/** Oublie le compte sur cet appareil, sans rien supprimer côté serveur. */
export function deconnecter(): void {
  localStorage.removeItem(CLE_ID)
  localStorage.removeItem(CLE_PSEUDO)
  localStorage.removeItem("dofusidle.etat")
  localStorage.removeItem("dofusidle.version")
}
